// Phase 8 R2 — Header editor (name/description/category/active) + lines.

import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { useUpdateCannedJob } from '@/hooks/cannedJobs/useUpdateCannedJob';
import { useSetCannedJobActive } from '@/hooks/cannedJobs/useSetCannedJobActive';
import { CannedJobLinesEditor } from './CannedJobLinesEditor';
import { FavoriteToggle } from './FavoriteToggle';
import type { CannedJobAdminItem } from '@/hooks/cannedJobs/useCannedJobsAdmin';

interface Props {
  job: CannedJobAdminItem;
}

export function CannedJobEditor({ job }: Props) {
  const [name, setName] = useState(job.name);
  const [description, setDescription] = useState(job.description ?? '');
  const [category, setCategory] = useState(job.category ?? '');

  // Reset form when switching jobs.
  useEffect(() => {
    setName(job.name);
    setDescription(job.description ?? '');
    setCategory(job.category ?? '');
  }, [job.id, job.name, job.description, job.category]);

  const updateMut = useUpdateCannedJob();
  const activeMut = useSetCannedJobActive();

  const headerDirty =
    name !== job.name ||
    (description ?? '') !== (job.description ?? '') ||
    (category ?? '') !== (job.category ?? '');

  const canSaveHeader = headerDirty && name.trim().length > 0;

  const handleSaveHeader = async () => {
    if (!canSaveHeader) return;
    try {
      await updateMut.mutateAsync({
        cannedJobId: job.id,
        name: name.trim(),
        description: description.trim() || null,
        category: category.trim() || null,
      });
    } catch {
      // toast in hook
    }
  };

  const handleToggleActive = async (next: boolean) => {
    try {
      await activeMut.mutateAsync({ cannedJobId: job.id, active: next });
    } catch {
      // toast in hook
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              {job.name}
              <Badge variant={job.is_active ? 'default' : 'secondary'} className="text-[10px]">
                {job.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </CardTitle>
            <CardDescription>
              Edit template metadata. Changes do not affect previously applied tickets.
            </CardDescription>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <FavoriteToggle
              shopId={job.shop_id}
              cannedJobId={job.id}
              isFavorite={job.is_favorite}
              size={18}
            />
            <div className="flex items-center gap-2">
              <Label htmlFor="active-toggle" className="text-xs text-muted-foreground">
                {job.is_active ? 'Active' : 'Inactive'}
              </Label>
              <Switch
                id="active-toggle"
                checked={job.is_active}
                disabled={activeMut.isPending}
                onCheckedChange={handleToggleActive}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="cj-name">Name</Label>
              <Input
                id="cj-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Front Brake Service"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cj-category">Category</Label>
              <Input
                id="cj-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. Brakes"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="cj-desc">Description</Label>
            <Textarea
              id="cj-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional notes about this canned job"
              rows={2}
            />
          </div>
          <div className="flex justify-end pt-1">
            <Button
              type="button"
              onClick={handleSaveHeader}
              disabled={!canSaveHeader || updateMut.isPending}
              size="sm"
            >
              {updateMut.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save Header
            </Button>
          </div>
        </CardContent>
      </Card>

      <CannedJobLinesEditor
        cannedJobId={job.id}
        jobIsActive={job.is_active}
        cannedCategory={job.category ?? null}
      />
    </div>
  );
}
